// LeadsTable.js — typing-snappy + Lead ID search + latest-updated sort + optimistic save + live revalidate
import React, {
  useEffect,
  useMemo,
  useState,
  useDeferredValue,
  startTransition,
  useCallback,
  useRef
} from 'react';
import {
  Box, Typography, Table, TableHead, TableRow, TableCell,
  TableBody, TextField, Select, MenuItem, InputLabel, FormControl,
  IconButton, Dialog, DialogTitle, DialogContent, Grid, Checkbox, Button, Popover,
  InputAdornment, TablePagination, Tooltip, Alert
} from '@mui/material';
import { Link as MUILink } from '@mui/material';
import VisibilityIcon from '@mui/icons-material/Visibility';
import EditIcon from '@mui/icons-material/Edit';
import ViewColumnIcon from '@mui/icons-material/ViewColumn';
import SearchIcon from '@mui/icons-material/Search';
import ClearIcon from '@mui/icons-material/Clear';
import HistoryIcon from '@mui/icons-material/History';
import EventIcon from '@mui/icons-material/Event';
import RefreshIcon from '@mui/icons-material/Refresh';
import { createTheme, ThemeProvider } from '@mui/material/styles';
import { useAuth } from './AuthContext';
import '@fontsource/montserrat';
import LoadingOverlay from './LoadingOverlay';
import CalendarView from './CalendarView';
import MobileActionMenu from './MobileActionMenu';
import { CRM_TABLE_SX, crmRowUpdatedAt, latestCrmRows, parseCrmTimestamp } from '../utils/crmTableUtils';
import { getLeadSaveMessage } from '../utils/leadTransferStatus';

/* ---------- small debounce helper (no extra deps) ---------- */
function useDebouncedValue(value, delay = 250) {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const t = setTimeout(() => setDebounced(value), delay);
    return () => clearTimeout(t);
  }, [value, delay]);
  return debounced;
}

async function fetchJsonArray(url, label) {
  const res = await fetch(url);
  const data = await res.json();
  if (!res.ok) {
    throw new Error(data?.error || `${label} failed with status ${res.status}`);
  }
  if (!Array.isArray(data)) {
    throw new Error(data?.error || `${label} did not return a row array`);
  }
  return data;
}

/* ---------- URL helpers ---------- */
const looksLikeUrl = (val = '') =>
  /^https?:\/\/|^www\.|^[\w-]+\.[\w.-]+(\/|$)/i.test(String(val).trim());

const normalizeUrl = (val = '') => {
  const v = String(val).trim();
  if (!v) return '';
  return /^https?:\/\//i.test(v) ? v : `https://${v}`;
};
const displayUrl = (val = '') => String(val).trim().replace(/^https?:\/\//i, '');

const isMobileColumn = (key = '') => {
  const normalizedKey = String(key || '').toLowerCase();
  return normalizedKey.includes('mobile') || normalizedKey.includes('phone');
};

/* ---------- date/sort helpers ---------- */
const lastUpdatedMillis = (row = {}) => crmRowUpdatedAt(row, ['Lead Updated Time']);

/* ---------- key helper: Lead ID primary, Mobile fallback ---------- */
const getLeadKey = (row = {}) =>
  String(row['Lead ID'] || row['Mobile Number'] || '').trim();

/* ---------- theme ---------- */
const theme = createTheme({
  typography: {
    fontFamily: 'Montserrat, sans-serif',
    fontSize: 10.5
  }
});

const selectorStyle = {
  fontFamily: 'Montserrat, sans-serif',
  fontSize: 8
};

/* =====================================
   Edit Dialog (isolated to avoid table re-renders)
   ===================================== */
const EditLeadDialog = React.memo(function EditLeadDialog({
  open,
  row,
  validationOptions,
  onClose,
  onSubmit,
  submitting
}) {
  const [form, setForm] = useState(() => row || {});
  useEffect(() => { setForm(row || {}); }, [row]);

  const handleChange = useCallback((e) => {
    const { name, value } = e.target;
    setForm(prev => ({ ...prev, [name]: value }));
  }, []);

  const handleSubmit = useCallback((e) => {
    e.preventDefault();
    onSubmit({
      ...form,
      'Lead Updated Time': new Date().toLocaleString('en-GB', { hour12: false })
    });
  }, [onSubmit, form]);

  return (
    <Dialog open={open} onClose={submitting ? undefined : onClose} maxWidth="md" fullWidth keepMounted>
      <DialogTitle>Edit Lead</DialogTitle>
      <form onSubmit={handleSubmit} onKeyDown={(e) => {
        if (submitting && (e.key === 'Enter' || e.key === 'NumpadEnter')) e.preventDefault();
      }}>
        <DialogContent dividers>
          <Grid container spacing={2}>
            {row && Object.keys(row).map((key, i) => (
              <Grid item xs={6} key={key}>
                {validationOptions?.[key] ? (
                  <FormControl fullWidth size="small" disabled={submitting}>
                    <InputLabel>{key}</InputLabel>
                    <Select
                      name={key}
                      value={form[key] ?? ''}
                      onChange={handleChange}
                      label={key}
                      MenuProps={{ disablePortal: true }}
                    >
                      {validationOptions[key].map((opt, idx) => (
                        <MenuItem key={idx} value={opt}>{opt}</MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                ) : (
                  <TextField
                    fullWidth
                    label={key}
                    name={key}
                    value={form[key] ?? ''}
                    onChange={handleChange}
                    size="small"
                    disabled={submitting}
                    autoFocus={i === 0}
                    inputProps={{ autoComplete: 'off', spellCheck: 'false' }}
                  />
                )}
              </Grid>
            ))}
          </Grid>

          <Box mt={3} display="flex" justifyContent="flex-end" gap={1}>
            <Button onClick={onClose} disabled={submitting} variant="text">Cancel</Button>
            <Button type="submit" variant="contained" sx={{ backgroundColor: '#6495ED' }} disabled={submitting}>
              {submitting ? 'Saving…' : 'Save Changes'}
            </Button>
          </Box>
        </DialogContent>
      </form>
    </Dialog>
  );
});

const LeadsTable = () => {
  const [leads, setLeads] = useState([]);
  const [loading, setLoading] = useState(true);

  // live search state (deferred + debounced)
  const [searchInput, setSearchInput] = useState('');
  const deferredSearchInput = useDeferredValue(searchInput);
  const debouncedSearch = useDebouncedValue(deferredSearchInput, 200);

  // Lead ID quick finder (separate to keep typing ultra-fast)
  const [leadIdInput, setLeadIdInput] = useState('');
  const deferredLeadId = useDeferredValue(leadIdInput);
  const debouncedLeadId = useDebouncedValue(deferredLeadId, 180);

  const [filterStatus, setFilterStatus] = useState('');
  const [filterSource, setFilterSource] = useState('');
  const [filterOwner, setFilterOwner] = useState('');
  const [sortConfig, setSortConfig] = useState({ key: 'Timestamp', direction: 'desc' }); // default: latest updated first
  const [visibleColumns, setVisibleColumns] = useState([]);
  const [anchorEl, setAnchorEl] = useState(null);
  const [viewRow, setViewRow] = useState(null);
  const [editRow, setEditRow] = useState(null);
  const [validationOptions, setValidationOptions] = useState({});
  const [loadError, setLoadError] = useState('');
  const [selectedEntryRow, setSelectedEntryRow] = useState(null);
  const [showCalendarModal, setShowCalendarModal] = useState(false);
  const [entryType, setEntryType] = useState('');

  // submit guard
  const [submitting, setSubmitting] = useState(false);

  // pagination
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(100);

  // Logs modal states
  const [leadLogs, setLeadLogs] = useState([]);
  const [logColumns, setLogColumns] = useState([]);
  const [logsOpen, setLogsOpen] = useState(false);
  const [allLeads, setAllLeads] = useState([]);
  const [logsKeyInfo, setLogsKeyInfo] = useState({ mobile: '', name: '', company: '' });

  const { user } = useAuth();
  const username = user?.username;
  const role = user?.role;

  // URLs
  const dataUrl = '/api/leads';
  const formSubmitUrl = '/api/leads';
  const validationUrl = '/api/leads?action=validation';

  const fetchAbortRef = useRef(null);
  const revalidate = useCallback(async () => {
    try {
      fetchAbortRef.current?.abort();
      const ctrl = new AbortController();
      fetchAbortRef.current = ctrl;
      const res = await fetch(dataUrl, { signal: ctrl.signal });
      const data = await res.json();
      if (!res.ok) throw new Error(data?.error || `Leads failed with status ${res.status}`);
      if (!Array.isArray(data)) throw new Error(data?.error || 'Leads did not return a row array');
      setLoadError('');
      const filteredData = role === 'End User'
        ? data.filter(lead => lead['Lead Owner'] === username)
        : data;

      const deduped = latestCrmRows(filteredData, getLeadKey, ['Lead Updated Time']);
      startTransition(() => {
        setAllLeads(filteredData);
        setLeads(deduped);
      });
    } catch (e) {
      // ignore aborts
      if (e?.name !== 'AbortError') console.error('revalidate failed', e);
    }
  }, [dataUrl, role, username]);

  useEffect(() => {
    let cancelled = false;

    (async () => {
      try {
        const data = await fetchJsonArray(dataUrl, 'Leads');

        const filteredData = role === 'End User'
          ? data.filter(lead => lead['Lead Owner'] === username)
          : data;

        if (cancelled) return;
        setLoadError('');

        setAllLeads(filteredData);

        const deduplicated = latestCrmRows(filteredData, getLeadKey, ['Lead Updated Time']);
        setLeads(deduplicated);

        setVisibleColumns(
          JSON.parse(localStorage.getItem(`visibleColumns-v2-${username}-leads`)) ||
          ['Lead ID', 'Company', 'First Name', 'Mobile Number', 'Lead Status', 'Lead Owner'].filter((key) => Object.keys(deduplicated[0] || {}).includes(key))
        );
      } catch (e) {
        console.error('Failed to fetch leads', e);
        if (!cancelled) setLoadError(e.message || 'Failed to fetch leads');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();

    (async () => {
      try {
        const res = await fetch(validationUrl);
        const json = await res.json();
        if (!res.ok) throw new Error(json?.error || `Validation failed with status ${res.status}`);
        if (!cancelled) setValidationOptions(json);
      } catch (e) {
        console.error('Failed to fetch validation options', e);
      }
    })();

    return () => { cancelled = true; fetchAbortRef.current?.abort(); };
  }, [username, role]);

  const handleSort = (key) => {
    setSortConfig(prev => ({
      key,
      direction: prev.key === key && prev.direction === 'asc' ? 'desc' : 'asc'
    }));
  };

  /* ---------- memoized unique values for dropdowns ---------- */
  const uniqueValues = useMemo(() => {
    const status = new Set();
    const source = new Set();
    const owner = new Set();
    for (const d of leads) {
      if (d['Lead Status']) status.add(d['Lead Status']);
      if (d['Lead Source']) source.add(d['Lead Source']);
      if (d['Lead Owner']) owner.add(d['Lead Owner']);
    }
    return {
      status: [...status],
      source: [...source],
      owner: [...owner]
    };
  }, [leads]);

  /* ---------- index map for O(1) exact Lead ID lookups ---------- */
  const leadIdIndex = useMemo(() => {
    const m = new Map();
    for (const d of leads) {
      const id = String(d['Lead ID'] || '').trim();
      if (id) m.set(id.toLowerCase(), d);
    }
    return m;
  }, [leads]);

  /* ---------- filters + sorting ---------- */
  const filteredLeads = useMemo(() => {
    const q = (debouncedSearch || '').toLowerCase().trim();
    const idq = (debouncedLeadId || '').toLowerCase().trim();

    let base = leads;

    // Lead ID fast path
    if (idq) {
      const exact = leadIdIndex.get(idq);
      if (exact) {
        base = [exact];
      } else {
        base = leads.filter(l => String(l['Lead ID'] || '').toLowerCase().includes(idq));
      }
    }

    const filtered = base.filter(lead => {
      const matchesSearch = !q || ['Lead ID', 'First Name', 'Last Name', 'Company', 'Mobile Number'].some(k =>
        String(lead[k] || '').toLowerCase().includes(q)
      );
      const matchesStatus = !filterStatus || lead['Lead Status'] === filterStatus;
      const matchesSource = !filterSource || lead['Lead Source'] === filterSource;
      const matchesOwner  = !filterOwner  || lead['Lead Owner']  === filterOwner;
      return matchesSearch && matchesStatus && matchesSource && matchesOwner;
    });

    const { key, direction } = sortConfig || {};

    // Default/explicit: sort by latest updated first when key is 'Timestamp'
    const sorted = [...filtered].sort((a, b) => {
      if (!key || key === 'Timestamp') {
        const difference = direction === 'asc'
          ? lastUpdatedMillis(a) - lastUpdatedMillis(b)
          : lastUpdatedMillis(b) - lastUpdatedMillis(a);
        return difference;
      }
      if (key === 'Updated At' || key === 'Lead Updated Time' || key === 'Created Time') {
        const av = parseCrmTimestamp(a[key]);
        const bv = parseCrmTimestamp(b[key]);
        return direction === 'asc' ? av - bv : bv - av;
      }
      // string-ish fallback
      const aVal = a[key] ?? '';
      const bVal = b[key] ?? '';
      const cmp = String(aVal).localeCompare(String(bVal), undefined, { numeric: true, sensitivity: 'base' });
      return direction === 'asc' ? cmp : -cmp;
    });

    return sorted;
  }, [leads, debouncedSearch, debouncedLeadId, filterStatus, filterSource, filterOwner, sortConfig, leadIdIndex]);

  /* ---------- pagination (and cap while searching) ---------- */
  const pagedRows = useMemo(() => {
    const searching = (debouncedSearch || '').trim() || (debouncedLeadId || '').trim();
    if (searching) return filteredLeads.slice(0, 300); // extra snappy while typing
    const start = page * rowsPerPage;
    return filteredLeads.slice(start, start + rowsPerPage);
  }, [filteredLeads, page, rowsPerPage, debouncedSearch, debouncedLeadId]);

  const handleColumnToggle = (col) => {
    setVisibleColumns(prev => {
      const updated = prev.includes(col) ? prev.filter(c => c !== col) : [...prev, col];
      localStorage.setItem(`visibleColumns-v2-${username}-leads`, JSON.stringify(updated));
      return updated;
    });
  };

  const handleSelectAll = () => {
    const all = Object.keys(leads[0] || {});
    setVisibleColumns(all);
    localStorage.setItem(`visibleColumns-v2-${username}-leads`, JSON.stringify(all));
  };

  const handleDeselectAll = () => {
    setVisibleColumns([]);
    localStorage.setItem(`visibleColumns-v2-${username}-leads`, JSON.stringify([]));
  };

  /* ---------- Logs: show full rows, newest first ---------- */
  const preferredOrderBoost = (columns) => {
    const priority = [
      'Timestamp',
      'Lead Updated Time',
      'Lead ID',
      'Lead Status',
      'Lead Owner',
      'Remarks',
      'Quotation Link',
      'First Name',
      'Last Name',
      'Company',
      'Mobile Number',
      'Email ID'
    ];
    const set = new Set(priority);
    const prioritized = priority.filter(c => columns.includes(c));
    const rest = columns.filter(c => !set.has(c)).sort((a, b) => a.localeCompare(b));
    return [...prioritized, ...rest];
  };

  const handleViewLogs = (leadRow) => {
    const key = getLeadKey(leadRow);
    const logs = allLeads
      .filter(lead => getLeadKey(lead) === key)
      .sort((a, b) => (lastUpdatedMillis(b) - lastUpdatedMillis(a))); // newest first by effective time

    // Dynamic union of columns
    const colSet = new Set();
    logs.forEach(row => Object.keys(row || {}).forEach(k => colSet.add(k)));
    const allCols = preferredOrderBoost(Array.from(colSet));

    setLogsKeyInfo({
      mobile: leadRow['Mobile Number'] || '',
      name: [leadRow['First Name'], leadRow['Last Name']].filter(Boolean).join(' ').trim(),
      company: leadRow['Company'] || ''
    });

    setLeadLogs(logs);
    setLogColumns(allCols);
    setLogsOpen(true);
  };

  const handleOpenMeetingFromRow = (row, type) => {
    setSelectedEntryRow(row);
    setEntryType(type); // 'Lead', 'Account', 'Deal'
    setShowCalendarModal(true);
  };

  // --- typing-friendly handlers ---
  const onSearchChange = useCallback((e) => {
    const v = e.target.value;
    setSearchInput(v);
    setPage(0);
  }, []);

  const setStatusFast = useCallback((v) => { startTransition(() => setFilterStatus(v)); setPage(0); }, []);
  const setSourceFast = useCallback((v) => { startTransition(() => setFilterSource(v)); setPage(0); }, []);
  const setOwnerFast  = useCallback((v) => { startTransition(() => setFilterOwner(v));  setPage(0); }, []);

  const handleOpenEdit = useCallback((row) => setEditRow(row), []);

  // Memoize the table body so unrelated state (like modal typing) doesn't rebuild rows
  const tableBodyMemo = useMemo(() => (
    <TableBody>
      {pagedRows.map((lead, index) => (
        <TableRow key={index}>
          {visibleColumns.map((key, i) => {
            const value = lead[key];
            if (key === 'Quotation Link' && looksLikeUrl(value)) {
              const href = normalizeUrl(value);
              return (
                <TableCell key={i}>
                  <MUILink
                    href={href}
                    target="_blank"
                    rel="noopener noreferrer"
                    underline="hover"
                    sx={{ wordBreak: 'break-all' }}
                  >
                    {displayUrl(value)}
                  </MUILink>
                </TableCell>
              );
            }
            if (isMobileColumn(key)) {
              return (
                <TableCell key={i}>
                  <MobileActionMenu mobile={value} />
                </TableCell>
              );
            }
            return <TableCell key={i}>{value}</TableCell>;
          })}
          <TableCell>
            <IconButton onClick={() => setViewRow(lead)} disabled={submitting}><VisibilityIcon /></IconButton>
            <IconButton onClick={() => handleOpenEdit(lead)} disabled={submitting}><EditIcon /></IconButton>
            <IconButton onClick={() => handleViewLogs(lead)} disabled={submitting}><HistoryIcon /></IconButton>
            <IconButton onClick={() => handleOpenMeetingFromRow(lead, 'Lead')} disabled={submitting}><EventIcon /></IconButton>
          </TableCell>
        </TableRow>
      ))}
    </TableBody>
  ), [pagedRows, visibleColumns, submitting, handleOpenEdit]);

  return (
    <ThemeProvider theme={theme}>
      {loading && <LoadingOverlay />}
      <Box padding={4}>
        {loadError && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {loadError}
          </Alert>
        )}
        <Box className="crm-page-header" display="flex" alignItems="center" justifyContent="space-between" mb={2}>
          <img className="crm-primary-logo" src="/assets/rido-sports-logo.png" alt="Rido Sports" />
          <Typography variant="h5" fontWeight="bold">Leads Records</Typography>
        </Box>

        <Box className="crm-filter-bar" display="flex" gap={2} mb={2} flexWrap="wrap" alignItems="center">
          {/* Search (deferred + debounced) */}
          <Box className="crm-search-tools" display="flex" alignItems="center" gap={1}>
            <TextField
              size="small"
              label="Search all lead fields"
              value={searchInput}
              onChange={onSearchChange}
              sx={{ minWidth: 240 }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon />
                  </InputAdornment>
                ),
                endAdornment: searchInput ? (
                  <InputAdornment position="end">
                    <IconButton
                      aria-label="clear search"
                      edge="end"
                      onClick={() => { setSearchInput(''); setPage(0); }}
                      size="small"
                    >
                      <ClearIcon fontSize="small" />
                    </IconButton>
                  </InputAdornment>
                ) : null
              }}
              inputProps={{ autoComplete: 'off', spellCheck: 'false', inputMode: 'search' }}
            />

            {/* Lead ID quick search (exact or partial) */}
            <TextField
              className="crm-secondary-search"
              size="small"
              label="Lead ID"
              value={leadIdInput}
              onChange={(e) => { startTransition(() => setLeadIdInput(e.target.value)); setPage(0); }}
              sx={{ minWidth: 160 }}
              placeholder="Exact or partial"
              inputProps={{ autoComplete: 'off', spellCheck: 'false' }}
            />

            {/* Manual refresh (non-blocking) */}
            <Tooltip title="Refresh data">
              <span>
                <IconButton onClick={() => startTransition(() => revalidate())} disabled={loading}>
                  <RefreshIcon />
                </IconButton>
              </span>
            </Tooltip>

            <Tooltip title="Choose columns">
              <IconButton onClick={(e) => setAnchorEl(e.currentTarget)} aria-label="Choose columns">
                <ViewColumnIcon />
              </IconButton>
            </Tooltip>
          </Box>

          {['Lead Status', 'Lead Source', 'Lead Owner'].map(filterKey => (
            <FormControl size="small" sx={{ minWidth: 160 }} key={filterKey}>
              <InputLabel>{filterKey}</InputLabel>
              <Select
                value={
                  filterKey === 'Lead Status' ? filterStatus :
                  filterKey === 'Lead Source' ? filterSource :
                  filterOwner
                }
                label={filterKey}
                onChange={e => {
                  const v = e.target.value;
                  if (filterKey === 'Lead Status') setStatusFast(v);
                  else if (filterKey === 'Lead Source') setSourceFast(v);
                  else setOwnerFast(v);
                }}
                MenuProps={{ disablePortal: true }}
              >
                <MenuItem value="">All</MenuItem>
                {(filterKey === 'Lead Status' ? uniqueValues.status
                  : filterKey === 'Lead Source' ? uniqueValues.source
                  : uniqueValues.owner).map(item => (
                    <MenuItem key={item} value={item}>{item}</MenuItem>
                  ))}
              </Select>
            </FormControl>
          ))}

          <Popover open={Boolean(anchorEl)} anchorEl={anchorEl} onClose={() => setAnchorEl(null)}>
            <Box padding={2} sx={selectorStyle}>
              <Button size="small" onClick={handleSelectAll}>Select All</Button>
              <Button size="small" onClick={handleDeselectAll}>Deselect All</Button>
              {Object.keys(leads[0] || {}).map(col => (
                <Box key={col}>
                  <Checkbox
                    size="small"
                    checked={visibleColumns.includes(col)}
                    onChange={() => handleColumnToggle(col)}
                  /> {col}
                </Box>
              ))}
            </Box>
          </Popover>
        </Box>

        <Box className="crm-table-shell">
        <Table size="small" sx={CRM_TABLE_SX}>
          <TableHead>
            <TableRow style={{ backgroundColor: '#6495ED' }}>
              {visibleColumns.map(header => (
                <TableCell
                  key={header}
                  onClick={() => handleSort(header)}
                  style={{ color: 'white', cursor: 'pointer' }}
                >
                  {header} {sortConfig.key === header ? (sortConfig.direction === 'asc' ? '↑' : '↓') : ''}
                </TableCell>
              ))}
              <TableCell style={{ color: 'white', fontWeight: 'bold' }}>Actions</TableCell>
            </TableRow>
          </TableHead>
          {tableBodyMemo}
        </Table>
        </Box>

        <TablePagination
          component="div"
          count={filteredLeads.length}
          page={page}
          onPageChange={(_, p) => setPage(p)}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={(e) => { setRowsPerPage(parseInt(e.target.value, 10)); setPage(0); }}
          rowsPerPageOptions={[50, 100, 200]}
        />

        {/* View Modal */}
        <Dialog open={!!viewRow} onClose={() => setViewRow(null)} maxWidth="md" fullWidth keepMounted>
          <DialogTitle>View Lead</DialogTitle>
          <DialogContent dividers>
            <Grid container spacing={2}>
              {viewRow && Object.entries(viewRow).map(([key, value]) => (
                <Grid item xs={6} key={key}>
                  {isMobileColumn(key) ? (
                    <Box>
                      <Typography variant="caption" sx={{ fontFamily: 'Montserrat, sans-serif', color: 'text.secondary' }}>
                        {key}
                      </Typography>
                      <Box mt={0.75}>
                        <MobileActionMenu mobile={value} />
                      </Box>
                    </Box>
                  ) : key === 'Quotation Link' && looksLikeUrl(value) ? (
                    <MUILink
                      href={normalizeUrl(value)}
                      target="_blank"
                      rel="noopener noreferrer"
                      underline="hover"
                      sx={{ display: 'inline-block', mt: 2, wordBreak: 'break-all' }}
                    >
                      {displayUrl(value)}
                    </MUILink>
                  ) : (
                    <TextField
                      fullWidth
                      size="small"
                      label={key}
                      value={value ?? ''}
                      InputProps={{ readOnly: true }}
                    />
                  )}
                </Grid>
              ))}
            </Grid>
          </DialogContent>
        </Dialog>

        {/* Logs Modal - FULL ROWS, NEWEST FIRST */}
        <Dialog open={logsOpen} onClose={() => setLogsOpen(false)} maxWidth="lg" fullWidth keepMounted>
          <DialogTitle>
            Lead Change Logs
            <Typography variant="body2" sx={{ mt: 0.5 }}>
              {logsKeyInfo.company ? `${logsKeyInfo.company} • ` : ''}
              {logsKeyInfo.name ? `${logsKeyInfo.name} • ` : ''}
              {logsKeyInfo.mobile ? `+91 ${logsKeyInfo.mobile}` : ''}
            </Typography>
          </DialogTitle>
          <DialogContent dividers>
            {leadLogs.length === 0 ? (
              <Typography>No logs found for this lead.</Typography>
            ) : (
              <Table size="small" sx={CRM_TABLE_SX}>
                <TableHead>
                  <TableRow>
                    {logColumns.map(col => (
                      <TableCell key={col} sx={{ fontWeight: 600 }}>{col}</TableCell>
                    ))}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {leadLogs.map((log, idx) => (
                    <TableRow key={idx}>
                      {logColumns.map((col) => {
                        const val = log[col] ?? '';
                        if (col === 'Quotation Link' && looksLikeUrl(val)) {
                          return (
                            <TableCell key={col}>
                              <MUILink
                                href={normalizeUrl(val)}
                                target="_blank"
                                rel="noopener noreferrer"
                                underline="hover"
                                sx={{ wordBreak: 'break-all' }}
                              >
                                {displayUrl(val)}
                              </MUILink>
                            </TableCell>
                          );
                        }
                        if (isMobileColumn(col)) {
                          return (
                            <TableCell key={col}>
                              <MobileActionMenu mobile={val} />
                            </TableCell>
                          );
                        }
                        return <TableCell key={col}>{String(val)}</TableCell>;
                      })}
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </DialogContent>
        </Dialog>

        {/* Edit Modal (isolated + memoized) */}
        <EditLeadDialog
          open={!!editRow}
          row={editRow}
          validationOptions={validationOptions}
          submitting={submitting}
          onClose={() => { if (!submitting) setEditRow(null); }}
          onSubmit={async (payload) => {
            if (submitting) return;
            setSubmitting(true);
            try {
              const res = await fetch(formSubmitUrl, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                  ...payload,
                  updatedByName: user?.username || user?.email || '',
                  updatedByEmail: user?.email || user?.username || ''
                })
              });
              const result = await res.json();
              if (!res.ok) throw new Error(result?.error || 'Lead update failed.');
              const saveStatus = getLeadSaveMessage(result, payload['Lead Status']);
              alert(`${saveStatus.confirmed ? '✅' : '⚠️'} ${saveStatus.message}`);
              setEditRow(null);
              setSortConfig({ key: 'Timestamp', direction: 'desc' });
              setPage(0);

              // 1) Optimistic local update so it reflects instantly
              const key = getLeadKey(payload);
              startTransition(() => {
                setLeads(prev => {
                  const next = prev.some(r => getLeadKey(r) === key)
                    ? prev.map(r => (getLeadKey(r) === key ? { ...r, ...payload } : r))
                    : [{ ...payload }, ...prev];
                  next.sort((a, b) => lastUpdatedMillis(b) - lastUpdatedMillis(a));
                  return next;
                });
                // also extend logs source so View Logs shows latest
                setAllLeads(prev => [...prev, payload]);
              });

              // 2) Background revalidate against the saved sheet row.
              void revalidate();

            } catch (err) {
              console.error(err);
              alert('❌ Error updating lead');
            } finally {
              setTimeout(() => setSubmitting(false), 0);
            }
          }}
        />

        {showCalendarModal && (
          <CalendarView
            open={showCalendarModal}
            onClose={() => setShowCalendarModal(false)}
            entryType={entryType}
            selectedEntryRow={selectedEntryRow}
            mode="existing"
          />
        )}
      </Box>
    </ThemeProvider>
  );
};

export default LeadsTable;
